import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';

interface LibrarySearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: () => void;
}

const LibrarySearchBar: React.FC<LibrarySearchBarProps> = ({
  value,
  onChange,
  onSearch,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (newValue: string) => {
    setLocalValue(newValue);
    
    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // Set new debounce timer (350ms)
    debounceTimer.current = setTimeout(() => {
      onChange(newValue);
    }, 350);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange('');
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      onChange(localValue);
      onSearch();
    }
  };

  return (
    <div className="relative w-full">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </div>
        <input
          type="text"
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search by title, author, subject, or call number…"
          className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder-gray-500"
          aria-label="Search library catalogue"
        />
        {localValue && (
          <button
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 rounded-r-lg"
            aria-label="Clear search"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );
};

export default LibrarySearchBar;